import { create } from "zustand";

/**
 * The one connection model every surface presents (CONTRACT C1), shared with
 * the native apps: what the app is, as distinct from what the socket last said.
 *
 * The socket reports what it is doing; the browser reports whether it has a
 * network; the strip above the composer and the sidebar label show the
 * debounced result, so a blip never flickers either of them.
 */
export type AppConnectionState =
  | "connected"
  /** The first connect of this organization. */
  | "connecting"
  /** The socket was lost and is being retried. */
  | "reconnecting"
  /** The browser says there is no network. */
  | "offline"
  /** The server speaks a newer protocol than this build. */
  | "updateRequired";

/** The strip's sentence; null when there is nothing to say. Same copy as the
 * native `AppConnectionState.stripText`, except the update line, which is the
 * web's own (a reload, not an app update). */
export function stripText(state: AppConnectionState): string | null {
  switch (state) {
    case "connected": return null;
    case "connecting":
    case "reconnecting": return "Reconnecting…";
    case "offline": return "You're offline";
    case "updateRequired": return "Rivets was updated — Reload";
  }
}

/** The sidebar's compact label. */
export function compactText(state: AppConnectionState): string | null {
  switch (state) {
    case "connected": return null;
    case "connecting": return "Connecting…";
    case "reconnecting": return "Reconnecting…";
    case "offline": return "Offline";
    case "updateRequired": return "Update required";
  }
}

export function workerOfflineText(name: string | undefined): string {
  return `${name?.trim() || "This task's worker"} is offline — work continues when it reconnects`;
}

/** A non-retryable `chat.subscribe_failed` code, as the chat's strip says
 * it (review #15). Mirrors native `ChatRefusal`. */
export function chatRefusal(code: string): "forbidden" | "unavailable" {
  return code === "forbidden" ? "forbidden" : "unavailable";
}

export function chatRefusalText(refusal: "forbidden" | "unavailable"): string {
  return refusal === "forbidden" ? "You no longer have access to this chat" : "This chat is unavailable";
}

/** A chat is shown "Catching up…" once it has been unsynced this long. */
export const CATCHING_UP_AFTER_MS = 2_000;

/**
 * What the app shows, debounced. A non-connected state is shown only once it
 * has held for `SHOW_AFTER_MS`, and once shown it stays for at least
 * `MINIMUM_SHOWN_MS` — the same rule on every client. `updateRequired` is
 * never debounced: it does not resolve by waiting.
 */
export class ConnectionDebouncer {
  static readonly SHOW_AFTER_MS = 2_000;
  static readonly MINIMUM_SHOWN_MS = 1_000;

  actual: AppConnectionState = "connected";
  actualSince: number;
  shown: AppConnectionState = "connected";
  shownSince: number;

  constructor(now: number) {
    this.actualSince = now;
    this.shownSince = now;
  }

  /** Records the actual state; returns what is shown now and when to ask
   * again (null: nothing pending). */
  report(next: AppConnectionState, now: number): { shown: AppConnectionState; recheckAt: number | null } {
    if (next !== this.actual) {
      this.actual = next;
      this.actualSince = now;
    }
    return this.evaluate(now);
  }

  evaluate(now: number): { shown: AppConnectionState; recheckAt: number | null } {
    if (this.actual === this.shown) return { shown: this.shown, recheckAt: null };
    if (this.actual === "updateRequired") return this.show(this.actual, now);
    // Something is showing: it stays its minimum, then follows.
    if (this.shown !== "connected") {
      const release = this.shownSince + ConnectionDebouncer.MINIMUM_SHOWN_MS;
      if (now < release) return { shown: this.shown, recheckAt: release };
      return this.show(this.actual, now);
    }
    // Connected is showing and something else is true: only once it has held.
    const due = this.actualSince + ConnectionDebouncer.SHOW_AFTER_MS;
    if (now < due) return { shown: this.shown, recheckAt: due };
    return this.show(this.actual, now);
  }

  private show(state: AppConnectionState, now: number) {
    this.shown = state;
    this.shownSince = now;
    return { shown: state, recheckAt: null };
  }
}

/** What the socket layer says it is doing. */
export type SocketPhase = "idle" | "connecting" | "open" | "closed" | "upgrade-required";

/**
 * The app's state from its inputs. `idle` before the first connect and
 * `connecting` before any handshake has completed are the first connect;
 * after one has, a closed or reconnecting socket is `reconnecting`.
 */
export function deriveConnectionState(input: { socket: SocketPhase; online: boolean; everConnected: boolean }): AppConnectionState {
  if (input.socket === "upgrade-required") return "updateRequired";
  if (!input.online) return "offline";
  if (input.socket === "open") return "connected";
  if (input.socket === "idle") return "connected";
  return input.everConnected ? "reconnecting" : "connecting";
}

interface ConnectionStatusState {
  /** The debounced state the strip and the sidebar show. */
  presented: AppConnectionState;
  /** The actual state, undebounced. The run clock reads both. */
  actual: AppConnectionState;
  /** Since when the relay socket has been open; null while it is not. */
  relayOpenSince: number | null;
  /** The last 50 transitions of the actual state, for "Copy diagnostics". */
  history: string[];
}

export const useConnectionStatus = create<ConnectionStatusState>(() => ({
  presented: "connected",
  actual: "connected",
  relayOpenSince: null,
  history: [],
}));

const HISTORY_LIMIT = 50;
let debouncer = new ConnectionDebouncer(Date.now());
let recheckTimer: ReturnType<typeof setTimeout> | null = null;
let clock: () => number = () => Date.now();

function apply(result: { shown: AppConnectionState; recheckAt: number | null }) {
  if (useConnectionStatus.getState().presented !== result.shown) useConnectionStatus.setState({ presented: result.shown });
  if (recheckTimer) clearTimeout(recheckTimer);
  recheckTimer = null;
  if (result.recheckAt === null) return;
  recheckTimer = setTimeout(() => {
    recheckTimer = null;
    apply(debouncer.evaluate(clock()));
  }, Math.max(0, result.recheckAt - clock()));
}

/** The socket layer reports the actual state on every change. */
export function reportConnection(state: AppConnectionState): void {
  const now = clock();
  const previous = debouncer.actual;
  if (state !== previous) {
    const line = `${new Date(now).toISOString()} ${previous} → ${state}`;
    const history = [...useConnectionStatus.getState().history, line].slice(-HISTORY_LIMIT);
    useConnectionStatus.setState({ actual: state, history });
  }
  apply(debouncer.report(state, now));
}

/** Whether the relay is open, and since when; provisional events only expire
 * while it is (web #10). */
export function reportRelayOpen(open: boolean): void {
  const current = useConnectionStatus.getState().relayOpenSince;
  if (open && current === null) useConnectionStatus.setState({ relayOpenSince: clock() });
  else if (!open && current !== null) useConnectionStatus.setState({ relayOpenSince: null });
}

/** Test seam. */
export function resetConnectionStatus(options: { now?: () => number } = {}): void {
  clock = options.now ?? (() => Date.now());
  if (recheckTimer) clearTimeout(recheckTimer);
  recheckTimer = null;
  debouncer = new ConnectionDebouncer(clock());
  useConnectionStatus.setState({ presented: "connected", actual: "connected", relayOpenSince: null, history: [] });
}

/**
 * When a run clock stops (CONTRACT C1): while nothing live can arrive — the
 * app not connected, or the chat's worker offline — it shows the time of the
 * last event received instead of counting up time nobody is working. Null
 * while it should tick.
 */
export function runClockFrozenAt(input: {
  live: boolean;
  workerOffline: boolean;
  startedAt: number;
  lastEventAt: number | undefined;
}): number | null {
  if (input.live && !input.workerOffline) return null;
  return Math.max(input.startedAt, input.lastEventAt ?? input.startedAt);
}
